<script module lang="ts">
  import type { NostrEvent, VerifiedEvent } from "@nostr/tools";

  let visible = $state<boolean>(false);
  let root = $state<NostrEvent | undefined>();
  let parent = $state<NostrEvent | undefined>();

  export function write(root_?: NostrEvent, parent_?: NostrEvent) {
    visible = true;
    root = root_;
    parent = parent_;
  }
</script>

<script lang="ts">
  import { slide } from "svelte/transition";
  import type { WindowNostr } from "@nostr/tools/nip07";
  import { pool } from "@nostr/gadgets/global";

  import { knownUsers, labels, relay } from "../relay.svelte";
  import { getTitle } from "../utils";
  import Link from "./Link.svelte";
  import type { NostrUser } from "@nostr/gadgets/metadata";

  let content = $state<string>("");
  let title = $state<string>("");
  let selectedLabel = $state<string>("");
  let isDragging = $state<boolean>(false);
  let startY = 0;
  let startHeight = 0;
  let containerHeight = $state<number>(400);

  let showAutocomplete = $state<boolean>(false);
  let suggestions = $state<NostrUser[]>([]);
  let selectedIndex = $state<number>(0);
  let cursorPosition = $state<number>(0);
  let textareaElement = $state<HTMLTextAreaElement | undefined>();

  async function getSuggestions(query: string): Promise<NostrUser[]> {
    return knownUsers.filter(
      (nu) =>
        nu.metadata.name?.toString?.()?.includes?.(query) ||
        nu.metadata.nip05?.toString?.()?.toLowerCase?.()?.includes(query)
    );
  }

  async function handleInput(ev: Event) {
    const textarea = ev.target as HTMLTextAreaElement;
    cursorPosition = textarea.selectionStart;

    const lastSpace = content.lastIndexOf(" ");
    const textBeforeCursor = content.slice(lastSpace, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex >= 0 && cursorPosition - lastAtIndex <= 20) {
      const query = textBeforeCursor.slice(lastAtIndex);
      suggestions = await getSuggestions(query.toLowerCase().trim());
      showAutocomplete = suggestions.length > 0;
      selectedIndex = 0;
    } else {
      showAutocomplete = false;
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      if (showAutocomplete) {
        showAutocomplete = false;
        e.preventDefault();
      } else {
        visible = false;
      }
    } else if (showAutocomplete) {
      switch (e.key) {
        case "ArrowDown":
          selectedIndex = (selectedIndex + 1) % suggestions.length;
          e.preventDefault();
          break;
        case "ArrowUp":
          selectedIndex =
            (selectedIndex - 1 + suggestions.length) % suggestions.length;
          e.preventDefault();
          break;
        case "Enter":
          if (showAutocomplete) {
            insertSuggestion("nostr:" + suggestions[selectedIndex].npub);
            e.preventDefault();
          }
          break;
        case "Tab":
          if (showAutocomplete) {
            insertSuggestion("nostr:" + suggestions[selectedIndex].npub);
            e.preventDefault();
          }
          break;
      }
    }
  }

  function insertSuggestion(suggestion: string) {
    const textBeforeCursor = content.slice(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");
    const textAfterCursor = content.slice(cursorPosition);

    content =
      textBeforeCursor.slice(0, lastAtIndex) + suggestion + textAfterCursor;
    showAutocomplete = false;

    setTimeout(() => {
      const newPosition = lastAtIndex + suggestion.length + 1;
      textareaElement!.setSelectionRange(newPosition, newPosition);
      textareaElement!.focus();
    }, 0);
  }

  function startDragging(e: MouseEvent) {
    isDragging = true;
    startY = e.clientY;
    startHeight = containerHeight;
    document.body.style.cursor = "row-resize";
  }

  function stopDragging() {
    isDragging = false;
    document.body.style.cursor = "";
  }

  function handleDrag(e: MouseEvent) {
    if (isDragging) {
      const deltaY = startY - e.clientY;
      containerHeight = Math.max(300, startHeight + deltaY);
    }
  }

  async function handleSubmit() {
    const event = await ((window as any).nostr as WindowNostr).signEvent({
      created_at: Math.round(Date.now() / 1000),
      kind: root ? 1111 : 11,
      tags: [
        ["-"],
        ["h", "_"],
        ...(title !== "" ? [["title", title]] : []),
        ...(root
          ? [
              ["K", "11"],
              ["E", root.id],
              ["P", root.pubkey]
            ]
          : []),
        ...(parent || root
          ? [
              ["k", (parent || root)!.kind.toString()],
              ["e", (parent || root)!.id],
              ["p", (parent || root)!.pubkey]
            ]
          : []),
        ...(selectedLabel !== "" ? [["t", selectedLabel]] : [])
      ],
      content: content.trim()
    });

    console.log("publishing", event);
    const r = await pool.ensureRelay(relay);
    try {
      try {
        await r.publish(event);
      } catch (err) {
        if (String(err).includes("Error: auth-required:")) {
          try {
            await r.auth(
              (auth) =>
                ((window as any).nostr as WindowNostr).signEvent(
                  auth
                ) as Promise<VerifiedEvent>
            );
          } catch (err) {
            console.warn("failed to auth", err);
            return;
          }

          await r.publish(event);
        } else {
          // rethrow here if it's not auth-required
          throw err;
        }
      }
    } catch (err) {
      // any non-auth-required errors will be caught here
      console.warn("failed to publish", err);
      return;
    }

    // clear stuff when we succeed in publishing
    content = "";
    title = "";
    selectedLabel = "";
    root = undefined;
    parent = undefined;
    visible = false;
  }
</script>

<svelte:window
  onkeydown={handleKeydown}
  onmousemove={handleDrag}
  onmouseup={stopDragging}
/>

{#if visible}
  <div
    class="fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-gray-800"
    style="height: {containerHeight}px"
    transition:slide={{ duration: 200 }}
  >
    <!-- Drag handle -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="absolute inset-x-0 -top-2 h-4 cursor-row-resize border-t-8 border-gray-200 dark:border-gray-700"
      onmousedown={startDragging}
    ></div>

    <div class="mx-auto h-full max-w-4xl px-4 py-8">
      <div class="flex h-full flex-col gap-4">
        {#if root}
          <div class="text-gray-700 dark:text-gray-300">
            <span class="text-orange-600">RE</span>:
            <Link
              class="cursor-pointer text-lg font-medium hover:text-blue-600 dark:hover:text-blue-400"
              href="/topic/{root.id}"
            >
              {getTitle(root)}
            </Link>
            {#if parent}
              <p class="mt-1 text-sm">
                replying to <Link
                  class="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                  href="/topic/{root?.id}#{parent.id}"
                >
                  <nostr-name pubkey={parent.pubkey}>{parent.pubkey}</nostr-name
                  >
                </Link>
              </p>
            {/if}
          </div>
        {:else}
          <input
            type="text"
            bind:value={title}
            placeholder="Title"
            class="w-full rounded border border-gray-300 bg-transparent p-4 text-lg text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:text-white dark:focus:border-blue-400"
          />
          <select
            bind:value={selectedLabel}
            class="w-full rounded border border-gray-300 bg-transparent p-4 text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:text-white dark:focus:border-blue-400"
          >
            <option value="">Select a label (optional)</option>
            {#each labels as label}
              <option value={label}>{label}</option>
            {/each}
          </select>
        {/if}

        <div class="relative flex-1">
          <textarea
            bind:value={content}
            bind:this={textareaElement}
            oninput={handleInput}
            placeholder="Write your post..."
            class="h-full w-full rounded border border-gray-300 bg-transparent p-4 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:text-white dark:focus:border-blue-400"
          ></textarea>

          {#if showAutocomplete}
            <div
              class="absolute z-10 max-h-48 w-64 overflow-y-auto rounded border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-700"
            >
              {#each suggestions as suggestion, i}
                <button
                  class="cursor-pointer px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 {i ===
                  selectedIndex
                    ? 'bg-gray-100 dark:bg-gray-600'
                    : ''}"
                  onclick={() => insertSuggestion("nostr:" + suggestion.npub)}
                >
                  {suggestion.shortName}
                </button>
              {/each}
            </div>
          {/if}
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2 text-sm text-gray-500">
            <!-- svelte-ignore a11y_consider_explicit_label -->
            <button
              class="rounded bg-gray-100 p-2 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>

            <!-- svelte-ignore a11y_consider_explicit_label -->
            <button
              class="rounded bg-gray-100 p-2 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div class="flex items-center gap-3">
            <button
              class="cursor-pointer rounded bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
              onclick={() => (visible = false)}
            >
              Cancel
            </button>
            <button
              class="cursor-pointer rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
              disabled={!content.trim() || (!root && !title.trim())}
              onclick={handleSubmit}
            >
              Publish
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
{/if}
